import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useGetCustomerOrdersQuery } from '../../services/api';
import { formatCurrency, formatDateTime, getStatusText, getStatusColor } from '../../utils/helpers';
import Loading from '../../components/Loading/Loading';
import { updateFaviconAndTitle, resetToDefault } from '../../utils/favicon';
import Error from '../../components/Error/Error';
import './CustomerOrders.css';

const CustomerOrders = () => {
  const { shopSlug } = useParams();
  const [email, setEmail] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');

  const { data, isLoading, error, refetch } = useGetCustomerOrdersQuery(
    { shopSlug, customerEmail: submittedEmail },
    { skip: !submittedEmail }
  );

   useEffect(() => {
    if (data?.data?.customer) {
      // You might want to get store info here or pass it differently
      // For now, we'll use a generic title
      updateFaviconAndTitle(null, `My Orders - ${shopSlug}`);
    }
  }, [data, shopSlug]);

  // Reset to default when component unmounts
  useEffect(() => {
    return () => {
      resetToDefault();
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email.trim()) {
      setSubmittedEmail(email.trim());
    }
  };

  const orders = data?.data?.orders || [];
  const customer = data?.data?.customer;

  return (
    <div className="customer-orders">
      <div className="container">
        <header className="orders-header">
          <h1>My Orders</h1>
          <p>View your order history for this restaurant</p>
        </header>

        {/* Email Input */}
        {!submittedEmail && (
          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSubmit} className="email-form">
                <div className="form-group">
                  <label className="form-label">
                    Enter your email address to view your orders
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-control"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary">
                  View Orders
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Orders List */}
        {submittedEmail && (
          <>
            {customer && (
              <div className="customer-info card">
                <div className="card-body">
                  <h3>Customer Information</h3>
                  <div className="customer-details">
                    <div><strong>Name:</strong> {customer.name}</div>
                    <div><strong>Email:</strong> {customer.email}</div>
                    <div><strong>Mobile:</strong> {customer.mobile}</div>
                  </div>
                </div>
              </div>
            )}

            {isLoading ? (
              <Loading text="Loading orders..." />
            ) : error ? (
              <Error 
                message="No orders found for this email" 
                onRetry={() => refetch()}
              />
            ) : (
              <div className="orders-list">
                <h2>Order History</h2>
                {orders.length === 0 ? (
                  <div className="empty-orders">
                    <p>No orders found for {submittedEmail}</p>
                    <p>Make sure you've entered the correct email address used for ordering.</p>
                  </div>
                ) : (
                  orders.map((order) => (
                    <div key={order._id} className="order-card card">
                      <div className="card-body">
                        <div className="order-header">
                          <div className="order-info">
                            <h3 className="order-number">Order #{order.orderNumber}</h3>
                            <div className="order-date">{formatDateTime(order.createdAt)}</div>
                          </div>
                          <div className="order-status">
                            <span className={`status-badge status-${getStatusColor(order.status)}`}>
                              {getStatusText(order.status)}
                            </span>
                          </div>
                        </div>

                        <div className="order-items">
                          <h4>Items:</h4>
                          {order.items.map((item, index) => (
                            <div key={index} className="order-item">
                              <span className="item-name">{item.nameSnapshot}</span>
                              <span className="item-quantity">× {item.qty}</span>
                              <span className="item-price">
                                {formatCurrency(item.priceSnapshot * item.qty)}
                              </span>
                            </div>
                          ))}
                        </div>

                        <div className="order-footer">
                          <div className="order-total">
                            Total: <strong>{formatCurrency(order.amount)}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            <div className="back-section">
              <button 
                onClick={() => {
                  setSubmittedEmail('');
                  setEmail('');
                }}
                className="btn btn-secondary"
              >
                Search Another Email
              </button>
              <a href={`/${shopSlug}`} className="btn btn-primary">
                Back to Menu
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerOrders;